import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { Delete, PersonAdd } from '@mui/icons-material'
import { toast } from 'react-toastify'
import {
  AUDITOR_ROLE_OPTIONS,
  AUDITOR_TYPE_OPTIONS,
  EVENT_TYPE_OPTIONS,
  defaultAuditorPermissions,
  normalizeAuditorPermissions,
  normalizeAuditorRoleIds,
  normalizeStringArray,
  toAuditorDateTimeString,
  toDateTimeLocal,
} from 'src/supportPortal/views/serviceApprovals/utils/auditorConstants'
import {
  assignAuditorRoles,
  listAuditorProfiles,
  searchAuditorPractitioners,
} from 'src/supportPortal/services/auditorManagementService'

function selectedFromCandidate(candidate) {
  if (!candidate) return null
  return {
    userId: candidate.userId || '',
    practitionerId: candidate.practitionerId || '',
    name: candidate.name || candidate.title || candidate.slug || 'Practitioner',
    slug: candidate.slug || '',
    email: candidate.email || '',
    mobile: candidate.mobile || '',
    auditorRoleIds: normalizeStringArray(candidate.auditorRoleIds),
    hasAuditorProfile: Boolean(candidate.hasAuditorProfile),
  }
}

function selectedFromFixedPractitioner(practitioner) {
  if (!practitioner) return null
  return {
    userId: practitioner.userId || '',
    practitionerId: practitioner.practitionerId || practitioner.id || '',
    name: practitioner.name || practitioner.title || practitioner.slug || 'Practitioner',
    slug: practitioner.slug || '',
    email: practitioner.email || '',
    mobile: practitioner.mobile || '',
    auditorRoleIds: normalizeStringArray(practitioner.auditorRoleIds),
    hasAuditorProfile:
      practitioner.auditorEligible === true ||
      normalizeStringArray(practitioner.auditorRoleIds).length > 0,
  }
}

function selectedFromProfileView(view) {
  if (!view) return null
  const practitioner = view.practitioner || {}
  const user = view.user || {}
  const profile = view.auditorProfile || {}
  return {
    userId: user.userId || profile.userId || '',
    practitionerId: practitioner.practitionerId || profile.practitionerId || '',
    name: practitioner.name || user.name || practitioner.title || 'Practitioner',
    slug: practitioner.slug || '',
    email: user.email || '',
    mobile: user.mobile || '',
    auditorRoleIds: normalizeStringArray(profile.auditorRoleIds),
    hasAuditorProfile: true,
  }
}

function permissionsFromProfile(profile) {
  if (Array.isArray(profile?.eventTypePermissions) && profile.eventTypePermissions.length > 0) {
    return profile.eventTypePermissions.map((permission) => ({
      roleId: permission.roleId || '',
      eventType: permission.eventType || '',
      defaultAuditorType: permission.defaultAuditorType || 'SUPERVISOR',
      roleExpiry: permission.roleExpiry || '',
      enabled: permission.enabled !== false,
    }))
  }
  return defaultAuditorPermissions(profile?.auditorRoleIds)
}

function practitionerSearchLabel(candidate) {
  if (!candidate) return ''
  const parts = []
  if (candidate.slug) parts.push(candidate.slug)
  if (candidate.email) parts.push(candidate.email)
  if (candidate.mobile) parts.push(candidate.mobile)
  return parts.length ? `${candidate.name} | ${parts.join(' | ')}` : candidate.name || ''
}

/**
 * The shared assign/edit form for practitioner auditor roles. Rendered inline both inside
 * the Service-Approvals dialog and as the "Assign Auditor Role" section of the audit hub.
 *
 * - searchEnabled: show the practitioner search (assign-from-scratch)
 * - fixedPractitioner: lock to one practitioner (no search) and prefill their saved profile
 * - existingProfile: edit an AuditorProfileView directly
 * - allowedRoleIds: restrict the selectable auditor roles (e.g. only the role relevant to the
 *   practitioner's base role); when set with no saved profile, it pre-selects those roles
 */
export default function AuditorAssignmentForm({
  fixedPractitioner = null,
  existingProfile = null,
  searchEnabled = false,
  allowedRoleIds = null,
  onSuccess,
  onCancel = null,
  submitLabel,
  secondaryAction = null,
}) {
  const [selected, setSelected] = useState(null)
  const [auditorRoleIds, setAuditorRoleIds] = useState([])
  const [permissions, setPermissions] = useState([])
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')

  const [practitionerSearch, setPractitionerSearch] = useState('')
  const [practitionerOptions, setPractitionerOptions] = useState([])
  const [loadingPractitioners, setLoadingPractitioners] = useState(false)
  const [prefilling, setPrefilling] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  const requestSeq = useRef(0)
  // Latest props read inside the init effect, which keys only on stable ids to avoid
  // re-running on every new object identity from the parent.
  const fixedRef = useRef(fixedPractitioner)
  const existingRef = useRef(existingProfile)
  const allowedRef = useRef(allowedRoleIds)
  fixedRef.current = fixedPractitioner
  existingRef.current = existingProfile
  allowedRef.current = allowedRoleIds

  const fixedPractitionerId = fixedPractitioner?.practitionerId || fixedPractitioner?.id || ''
  const existingProfileKey =
    existingProfile?.auditorProfile?.practitionerId ||
    existingProfile?.practitioner?.practitionerId ||
    ''
  const allowedKey = normalizeStringArray(allowedRoleIds).join(',')

  const roleOptions = useMemo(() => {
    const allow = normalizeStringArray(allowedRoleIds)
    return allow.length ? AUDITOR_ROLE_OPTIONS.filter((option) => allow.includes(option.id)) : AUDITOR_ROLE_OPTIONS
  }, [allowedRoleIds])

  function defaultRoleIdsFor(base) {
    const allow = normalizeStringArray(allowedRef.current)
    if (allow.length) return allow
    return normalizeStringArray(base?.auditorRoleIds)
  }

  // Pull the full saved profile (custom permissions/expiry) so opening on a practitioner who
  // already has a profile becomes a true edit instead of a clobber.
  async function applyProfileFor(practitionerId, base) {
    const fallbackRoles = defaultRoleIdsFor(base)
    if (!practitionerId) {
      setAuditorRoleIds(fallbackRoles)
      setPermissions(defaultAuditorPermissions(fallbackRoles))
      setNote('')
      return
    }
    setPrefilling(true)
    try {
      const all = await listAuditorProfiles()
      const match = all.find((view) => view?.practitioner?.practitionerId === practitionerId)
      const profile = match?.auditorProfile
      if (profile && normalizeStringArray(profile.auditorRoleIds).length > 0) {
        setAuditorRoleIds(normalizeStringArray(profile.auditorRoleIds))
        setPermissions(permissionsFromProfile(profile))
        setNote(profile.note || '')
      } else {
        setAuditorRoleIds(fallbackRoles)
        setPermissions(defaultAuditorPermissions(fallbackRoles))
        setNote('')
      }
    } finally {
      setPrefilling(false)
    }
  }

  useEffect(() => {
    setError(null)
    setReason('')
    setPractitionerSearch('')
    setPractitionerOptions([])

    const existing = existingRef.current
    const fixed = fixedRef.current

    if (existing) {
      const base = selectedFromProfileView(existing)
      setSelected(base)
      const profile = existing.auditorProfile || {}
      setAuditorRoleIds(normalizeStringArray(profile.auditorRoleIds))
      setPermissions(permissionsFromProfile(profile))
      setNote(profile.note || '')
      return
    }

    if (fixed) {
      const base = selectedFromFixedPractitioner(fixed)
      setSelected(base)
      applyProfileFor(base?.practitionerId, base)
      return
    }

    setSelected(null)
    setAuditorRoleIds([])
    setPermissions([])
    setNote('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixedPractitionerId, existingProfileKey, allowedKey])

  // Debounced practitioner search (assign-from-scratch mode only).
  useEffect(() => {
    if (!searchEnabled) return undefined
    const term = practitionerSearch.trim()
    if (term.length < 2) {
      setPractitionerOptions([])
      setLoadingPractitioners(false)
      return undefined
    }
    const seq = ++requestSeq.current
    setLoadingPractitioners(true)
    const timer = setTimeout(async () => {
      const results = await searchAuditorPractitioners(term)
      if (seq === requestSeq.current) {
        setPractitionerOptions(results)
        setLoadingPractitioners(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [searchEnabled, practitionerSearch])

  const selectedRoleIds = useMemo(() => normalizeStringArray(auditorRoleIds), [auditorRoleIds])

  function handleRolesChange(newRoleIds) {
    const nextRoleIds = newRoleIds.map((roleId) => roleId.id || roleId)
    setAuditorRoleIds(nextRoleIds)
    setPermissions((prev) => {
      const kept = (prev || []).filter((permission) => nextRoleIds.includes(permission.roleId))
      const keptKeys = new Set(kept.map((permission) => `${permission.roleId}:${permission.eventType}`))
      const additions = defaultAuditorPermissions(nextRoleIds).filter(
        (permission) => !keptKeys.has(`${permission.roleId}:${permission.eventType}`),
      )
      return [...kept, ...additions]
    })
  }

  function updatePermission(index, changes) {
    setPermissions((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...changes } : row)))
  }

  function addPermission() {
    const fallbackRoleId = selectedRoleIds[0] || roleOptions[0]?.id || AUDITOR_ROLE_OPTIONS[0].id
    const roleOption = AUDITOR_ROLE_OPTIONS.find((option) => option.id === fallbackRoleId)
    setPermissions((prev) => [
      ...prev,
      {
        roleId: fallbackRoleId,
        eventType: roleOption?.defaultEventType || 'therapy-session',
        defaultAuditorType: 'SUPERVISOR',
        roleExpiry: '',
        enabled: true,
      },
    ])
  }

  function removePermission(index) {
    setPermissions((prev) => prev.filter((_, rowIndex) => rowIndex !== index))
  }

  async function handleSubmit() {
    if (!selected?.practitionerId && !selected?.userId) {
      setError('Select a practitioner first.')
      return
    }
    const normalizedRoles = normalizeAuditorRoleIds(selectedRoleIds)
    if (normalizedRoles.length === 0) {
      setError('Select at least one auditor role.')
      return
    }
    const normalizedPermissions = normalizeAuditorPermissions(permissions, normalizedRoles)
    if (normalizedPermissions.length === 0) {
      setError('Add at least one auditor event permission.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const result = await assignAuditorRoles({
        userId: selected.userId || undefined,
        practitionerId: selected.practitionerId || undefined,
        auditorRoleIds: normalizedRoles,
        eventTypePermissions: normalizedPermissions,
        note: note?.trim() || undefined,
        reason: reason?.trim() || undefined,
      })
      if (result.ok) {
        toast.success(`Auditor role saved for ${selected.name}`)
        onSuccess?.()
      } else {
        setError(result.message)
        toast.error(result.message)
      }
    } catch (err) {
      const message = err?.message || 'Failed to save auditor role'
      setError(message)
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  const busy = submitting || prefilling

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {searchEnabled && (
        <Autocomplete
          fullWidth
          options={practitionerOptions}
          filterOptions={(options) => options}
          getOptionLabel={practitionerSearchLabel}
          isOptionEqualToValue={(option, value) => option.practitionerId === value.practitionerId}
          loading={loadingPractitioners}
          loadingText="Searching practitioners..."
          noOptionsText={
            practitionerSearch.trim().length < 2 ? 'Type at least 2 characters' : 'No practitioners found'
          }
          value={null}
          onChange={(event, candidate) => {
            const base = selectedFromCandidate(candidate)
            setSelected(base)
            if (base) {
              applyProfileFor(base.practitionerId, base)
            } else {
              setAuditorRoleIds([])
              setPermissions([])
              setNote('')
            }
          }}
          onInputChange={(event, value, changeReason) => {
            if (changeReason === 'input' || changeReason === 'clear') {
              setPractitionerSearch(value)
            }
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.practitionerId}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[option.slug, option.email, option.mobile].filter(Boolean).join(' | ')}
                  {option.hasAuditorProfile ? ' • existing auditor' : ''}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search practitioner (therapist / screener)"
              placeholder="Name, email or mobile..."
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPractitioners && <CircularProgress size={18} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {selected && (
        <Paper variant="outlined" sx={{ mt: searchEnabled ? 2 : 0, p: 2 }}>
          <Typography variant="subtitle2">{selected.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {[selected.slug, selected.email, selected.mobile].filter(Boolean).join(' | ') || 'Practitioner'}
          </Typography>
          {selected.hasAuditorProfile && (
            <Chip size="small" color="info" variant="outlined" label="Existing auditor" sx={{ ml: 1 }} />
          )}
        </Paper>
      )}

      {prefilling && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading current auditor settings...
          </Typography>
        </Box>
      )}

      {selected && !prefilling && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Pick the auditor role(s), then one permission per event type (auditor role, default
            Supervisor/Learner, optional expiry). This grants the role and creates/updates the auditor
            profile that makes them selectable as an auditor.
          </Alert>

          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={roleOptions}
            getOptionLabel={(option) => option.label || option.id}
            isOptionEqualToValue={(option, value) => option.id === (value.id || value)}
            value={AUDITOR_ROLE_OPTIONS.filter((option) => selectedRoleIds.includes(option.id))}
            onChange={(event, value) => handleRolesChange(value)}
            renderInput={(params) => (
              <TextField {...params} label="Auditor Roles" placeholder="Select auditor roles" required />
            )}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Event Permissions
          </Typography>
          <Stack spacing={1.5}>
            {permissions.length === 0 ? (
              <Alert severity="warning">Add at least one event permission.</Alert>
            ) : (
              permissions.map((permission, index) => (
                <Box
                  key={`${permission.roleId || 'role'}-${permission.eventType || 'event'}-${index}`}
                  sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
                >
                  <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <Autocomplete
                        size="small"
                        options={EVENT_TYPE_OPTIONS}
                        getOptionLabel={(option) => option.title || option.id || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        value={EVENT_TYPE_OPTIONS.find((option) => option.id === permission.eventType) || null}
                        onChange={(event, value) => updatePermission(index, { eventType: value?.id || '' })}
                        renderInput={(params) => <TextField {...params} label="Event Type" required />}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Auditor Role"
                        value={permission.roleId || ''}
                        onChange={(event) => updatePermission(index, { roleId: event.target.value })}
                        required
                      >
                        {AUDITOR_ROLE_OPTIONS.filter((option) => selectedRoleIds.includes(option.id)).map(
                          (option) => (
                            <MenuItem key={option.id} value={option.id}>
                              {option.label}
                            </MenuItem>
                          ),
                        )}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Default Type"
                        value={permission.defaultAuditorType || 'SUPERVISOR'}
                        onChange={(event) => updatePermission(index, { defaultAuditorType: event.target.value })}
                      >
                        {AUDITOR_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={10} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="datetime-local"
                        label="Role Expiry"
                        InputLabelProps={{ shrink: true }}
                        value={toDateTimeLocal(permission.roleExpiry)}
                        onChange={(event) =>
                          updatePermission(index, { roleExpiry: toAuditorDateTimeString(event.target.value) })
                        }
                        helperText="Blank = no expiry"
                      />
                    </Grid>
                    <Grid item xs={2} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        sx={{ m: 0 }}
                        control={
                          <Switch
                            size="small"
                            checked={permission.enabled !== false}
                            onChange={(event) => updatePermission(index, { enabled: event.target.checked })}
                          />
                        }
                        label=""
                      />
                      <IconButton size="small" onClick={() => removePermission(index)} aria-label="Remove permission">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Grid>
                  </Grid>
                </Box>
              ))
            )}
            <Box>
              <Button variant="outlined" size="small" onClick={addPermission} disabled={selectedRoleIds.length === 0}>
                Add Permission
              </Button>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Note (optional)"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Reason (optional)"
            placeholder="e.g., new_auditor, supervision"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: secondaryAction ? 'space-between' : 'flex-end',
          alignItems: 'center',
          gap: 1,
          mt: 2,
        }}
      >
        {secondaryAction && (
          <Button onClick={secondaryAction.onClick} disabled={busy}>
            {secondaryAction.label}
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onCancel && (
            <Button onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} /> : <PersonAdd />}
            onClick={handleSubmit}
            disabled={busy || !selected}
          >
            {submitLabel || (existingProfile ? 'Save Changes' : 'Assign Auditor Role')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
